<script>
  import Textfield from '@smui/textfield';
  import HelperText from '@smui/textfield/helper-text';
  import FormField from '@smui/form-field';
  import Checkbox from '@smui/checkbox';
  import { slide } from 'svelte/transition';
  import { onMount, untrack } from 'svelte';
  import * as yup from 'yup';
  import { deliveryAddress as schema } from './schemas.js';
  import { formdata } from './storage.js';

  let { valid = $bindable(), sameAddress = $bindable(), fields = $bindable(), attemptCount = 0 } = $props();

  let errors = $state({
    first_name: null,
    last_name: null,
    street: null,
    building: null,
    apartment: null,
    city: null,
    zipcode: null,
    country: null,
  });

  if ($formdata?.delivery_address_attributes) {
    sameAddress = false;
  }

  $effect(() => {
    valid = Object.keys(errors).filter(key => errors[key]).length === 0;
  });

  $effect(() => {
    if (sameAddress === false) {
      Object.keys(fields).forEach(fieldName => validateField(fieldName));
    } else if (sameAddress === true) {
      errors = {
        first_name: null,
        last_name: null,
        street: null,
        building: null,
        apartment: null,
        city: null,
        zipcode: null,
        country: null,
      };
    }
  });

  function validateField(fieldName) {
    try {
      schema.validateSyncAt(fieldName, fields);
      errors = { ...untrack(() => errors), [fieldName]: null };
    } catch (err) {
      errors = { ...untrack(() => errors), [fieldName]: err.message };
    }
  }

  onMount(() => {
    if (!sameAddress) {
      Object.keys(fields).forEach(fieldName => validateField(fieldName));
    }
  });

  let touched = $state(new Set());
  const show = (f) => errors[f] && (touched.has(f) || attemptCount > 0);
  const handleBlur = (f) => { touched = new Set([...touched, f]); validateField(f); };
  const handleInput = (f) => { validateField(f); };

</script>

<form>
  <div class="row row-1">
    <FormField>
      <Checkbox bind:checked={sameAddress} />
      {#snippet label()}Dostawa na ten sam adres.{/snippet}
    </FormField>
  </div>

  {#if !sameAddress}
    <div transition:slide>
      <div class="row">
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.first_name}>
            <Textfield bind:value={fields.first_name}
              variant="outlined"
              label="Imię"
              invalid={show('first_name')}
              input$autocomplete="given-name"
              onblur={() => handleBlur('first_name')}
              oninput={() => handleInput('first_name')}>
              {#snippet helper()}<HelperText persistent>{show('first_name') ? errors.first_name : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.last_name}>
            <Textfield bind:value={fields.last_name}
              variant="outlined"
              label="Nazwisko"
              invalid={show('last_name')}
              input$autocomplete="family-name"
              onblur={() => handleBlur('last_name')}
              oninput={() => handleInput('last_name')}>
              {#snippet helper()}<HelperText persistent invalid>{show('last_name') ? errors.last_name : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
      </div>

      <div class="row row-5 row-m3">
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.street}>
            <Textfield bind:value={fields.street}
              variant="outlined"
              label="Ulica"
              invalid={show('street')}
              input$autocomplete="address-line1"
              onblur={() => handleBlur('street')}
              oninput={() => handleInput('street')}>
              {#snippet helper()}<HelperText persistent invalid>{show('street') ? errors.street : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.building}>
            <Textfield bind:value={fields.building}
              variant="outlined"
              label="Budynek"
              invalid={show('building')}
              onblur={() => handleBlur('building')}
              oninput={() => handleInput('building')}>
              {#snippet helper()}<HelperText persistent invalid>{show('building') ? errors.building : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.apartment}>
            <Textfield bind:value={fields.apartment}
              variant="outlined"
              label="Lokal"
              invalid={show('apartment')}
              onblur={() => handleBlur('apartment')}
              oninput={() => handleInput('apartment')}>
              {#snippet helper()}<HelperText persistent invalid>{show('apartment') ? errors.apartment : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
      </div>

      <div class="row row-3 row-m2">
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.city}>
            <Textfield bind:value={fields.city}
              variant="outlined"
              label="Miejscowość"
              invalid={show('city')}
              input$autocomplete="address-line2"
              onblur={() => handleBlur('city')}
              oninput={() => handleInput('city')}>
              {#snippet helper()}<HelperText persistent invalid>{show('city') ? errors.city : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
        {#key attemptCount}
          <div class="cell" class:field-shaking={attemptCount > 0 && errors.zipcode}>
            <Textfield bind:value={fields.zipcode}
              variant="outlined"
              label="Kod pocztowy"
              invalid={show('zipcode')}
              input$autocomplete="postal-code"
              onblur={() => handleBlur('zipcode')}
              oninput={() => handleInput('zipcode')}>
              {#snippet helper()}<HelperText persistent invalid>{show('zipcode') ? errors.zipcode : ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        {/key}
      </div>

      {#if false}
        <!-- until we will start using countries / should be parametrised in shop settings -->
        <div class="row">
          <div class="cell">
            <Textfield bind:value={fields.country}
              variant="outlined"
              label="Kraj"
              invalid={errors.country}
              input$autocomplete="country">
              {#snippet helper()}<HelperText persistent invalid>{errors.country ?? ''}</HelperText>{/snippet}
            </Textfield>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</form>

<style>
  .field-shaking {
    animation: field-shake 0.35s ease;
  }

  @keyframes field-shake {
    0%, 100% { transform: translateX(0); }
    25%       { transform: translateX(-5px); }
    75%       { transform: translateX(5px); }
  }
</style>
